import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Smartphone, Award, Shield } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card.js';
import { Badge, DeviceStatusBadge, TelegramStatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { CopyButton } from '../../components/ui/CopyButton.js';
import { CardSkeleton, TableSkeleton } from '../../components/ui/Skeleton.js';
import { ErrorState } from '../../components/ui/EmptyState.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchUserById, fetchUserDevices, fetchUserEntitlements } from '../../api/users.js';
import { formatDate, formatRelativeTime } from '../../lib/utils.js';

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => fetchUserById(userId!),
    enabled: Boolean(userId),
  });

  const { data: devices, isLoading: isLoadingDevices } = useQuery({
    queryKey: ['admin-user-devices', userId],
    queryFn: () => fetchUserDevices(userId!),
    enabled: Boolean(userId),
  });

  const { data: entitlements, isLoading: isLoadingEntitlements } = useQuery({
    queryKey: ['admin-user-entitlements', userId],
    queryFn: () => fetchUserEntitlements(userId!),
    enabled: Boolean(userId),
  });

  if (isLoadingUser) {
    return <CardSkeleton />;
  }

  if (userError || !user) {
    return (
      <ErrorState
        title={t('userDetail.notFoundTitle')}
        message={userError?.message || `Unable to locate account: ${userId}`}
        onRetry={() => navigate('/admin/users')}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button and page title */}
      <div className="flex items-center gap-3">
        <Button size="sm" variant="outline" onClick={() => navigate('/admin/users')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> {t('userDetail.backButton')}
        </Button>
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            {t('userDetail.title')}
            <CopyButton value={user.id} truncate={false} className="text-sm font-mono text-indigo-600 dark:text-indigo-400" />
          </h2>
        </div>
      </div>

      {/* Privacy Notice Banner */}
      <div className="p-4 rounded-xl border border-indigo-200 dark:border-indigo-900/60 bg-indigo-50/50 dark:bg-indigo-950/20 text-indigo-900 dark:text-indigo-200 text-xs flex items-start gap-3">
        <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold block">{t('userDetail.privacyNoticeTitle')}</span>
          {t('userDetail.privacyNoticeDesc')}
        </div>
      </div>

      {/* Account Overview Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('userDetail.accountOverview')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs">
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">ID</span>
              <CopyButton value={user.id} truncate={false} />
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('userDetail.accountStatus')}</span>
              <Badge variant={user.status === 'active' ? 'success' : 'danger'}>{user.status}</Badge>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('userDetail.phoneNumber')}</span>
              <span className="font-mono font-medium text-slate-900 dark:text-slate-100">
                {user.phone_number || t('users.noPhone')}
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('userDetail.telegramChatId')}</span>
              <div>
                <TelegramStatusBadge chatId={user.telegram_chat_id} />
              </div>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('users.tableLastLogin')}</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {user.last_login_at || user.updated_at
                  ? `${formatRelativeTime(user.last_login_at || user.updated_at)} (${formatDate(user.last_login_at || user.updated_at)})`
                  : t('users.noLoginRecorded')}
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Country</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">{user.country || 'Unknown'}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Timezone</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">{user.timezone || 'UTC'}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('userDetail.registeredAt')}</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">{formatDate(user.created_at)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Active Entitlements Card */}
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" />
              {t('userDetail.entitlementsTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingEntitlements ? (
              <TableSkeleton rows={3} cols={2} />
            ) : !entitlements || entitlements.length === 0 ? (
              <p className="text-xs text-slate-400 italic">{t('userDetail.noEntitlements')}</p>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                {entitlements.map((ent) => (
                  <div key={ent.id} className="py-2.5 flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-slate-900 dark:text-slate-100 font-mono">
                        {ent.entitlement}
                      </span>
                      <span className="block text-[11px] text-slate-400">
                        Source: {ent.source} • Expires: {ent.expires_at ? formatDate(ent.expires_at) : 'Never'}
                      </span>
                    </div>
                    <Badge variant={ent.status === 'active' ? 'success' : 'neutral'}>
                      {ent.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Linked Devices Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-indigo-600" />
            {t('userDetail.registeredDevices')} ({devices?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoadingDevices ? (
            <div className="p-4">
              <TableSkeleton rows={2} cols={4} />
            </div>
          ) : !devices || devices.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400">
              {t('userDetail.noDevices')}
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase">
                <tr>
                  <th className="py-3 px-4">{t('devices.tableDeviceId')}</th>
                  <th className="py-3 px-4">{t('devices.tableModelPlatform')}</th>
                  <th className="py-3 px-4">{t('devices.tableAppVersion')}</th>
                  <th className="py-3 px-4">{t('devices.tableStatus')}</th>
                  <th className="py-3 px-4">{t('devices.tableLastSeen')}</th>
                  <th className="py-3 px-4 text-right">{t('users.tableActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {devices.map((dev) => (
                  <tr key={dev.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-3 px-4">
                      <CopyButton value={dev.id} truncate={true} />
                    </td>
                    <td className="py-3 px-4 font-medium">
                      {dev.device_model || dev.platform} ({dev.os_version || dev.platform})
                    </td>
                    <td className="py-3 px-4 font-mono">v{dev.app_version}</td>
                    <td className="py-3 px-4">
                      <DeviceStatusBadge
                        isOnline={Boolean(dev.last_seen_at && Date.now() / 1000 - dev.last_seen_at < 300)}
                        isRevoked={Boolean(dev.revoked_at)}
                      />
                    </td>
                    <td className="py-3 px-4 text-slate-500">
                      {formatRelativeTime(dev.last_seen_at)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/admin/devices/${dev.id}`)}
                      >
                        {t('common.details')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
